import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquareText, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

function SayMoreIcon({ size = 18, color = 'white' }) {
  const fill = color === 'white' ? 'rgba(255,255,255,0.25)' : 'rgba(214,58,180,0.15)';
  return (
    <svg viewBox="0 0 24 24" fill="none" width={size} height={size}>
      <rect x="9" y="3" width="6" height="10" rx="3" fill={fill} stroke={color} strokeWidth="1.5" />
      <path d="M5 11a7 7 0 0 0 14 0" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 18v3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 21h8" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M19 7c1 1 1.5 2.5 1.5 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

const AI_DEMO_STORAGE_PATH = String(import.meta.env?.VITE_HERO_AI_DEMO_STORAGE_PATH || '').trim();
const AI_DEMO_VIDEO_URL = String(import.meta.env?.VITE_HERO_AI_DEMO_VIDEO_URL || '').trim();

export default function HeroAIDemoPanel({ compact = false, titleOverride = '' }) {
  const { t } = useTranslation('pages');
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const resolveVideo = async () => {
      if (AI_DEMO_VIDEO_URL) {
        setVideoUrl(AI_DEMO_VIDEO_URL);
        setHasVideo(true);
        return;
      }

      if (!AI_DEMO_STORAGE_PATH) {
        setHasVideo(false);
        setVideoUrl('');
        return;
      }

      setLoading(true);
      try {
        const resolvedUrl = await getDownloadURL(ref(storage, AI_DEMO_STORAGE_PATH));
        if (!mounted) return;
        setVideoUrl(resolvedUrl);
        setHasVideo(true);
      } catch {
        if (!mounted) return;
        setVideoUrl('');
        setHasVideo(false);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    resolveVideo();
    return () => {
      mounted = false;
    };
  }, []);

  const brand = useMemo(() => ({
    magenta: '#D63AB4',
    magentaDark: '#B2288E',
    cream: '#FDFBF7',
    stone900: '#1c1917',
    stone600: '#57534e',
    stone500: '#78716c',
    stone200: '#e7e5e4',
    white: '#FFFFFF',
  }), []);

  const handleStartPlayback = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      setIsPlaying(true);
      await video.play();
    } catch {
      setIsPlaying(false);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: compact ? '420px' : '760px',
        background: 'linear-gradient(180deg, rgba(255,251,247,0.98), rgba(248,242,236,0.98))',
        borderRadius: '2rem',
        padding: compact ? '1.1rem' : '1.5rem',
        border: '1px solid rgba(231,229,228,0.92)',
        boxShadow: '0 26px 60px -34px rgba(28,25,23,0.38)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.9rem',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #D63AB4, #e86cc8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(214,58,180,0.18)',
            flexShrink: 0,
          }}
        >
          <SayMoreIcon size={18} color="white" />
        </div>
        <div>
          <p
            style={{
              margin: 0,
              fontFamily: '"Figtree", system-ui, sans-serif',
              fontSize: '0.75rem',
              letterSpacing: '0.06em',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: brand.magenta,
            }}
          >
            {t('hero.demoCards.ai.badge')}
          </p>
          <h3
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: compact ? '1.12rem' : '1.5rem',
              fontWeight: 600,
              color: brand.stone900,
            }}
          >
            {titleOverride || t('hero.demoCards.ai.panelTitle', 'Say More! demo')}
          </h3>
        </div>
      </div>

      <div style={{
        margin: '6px 0 16px',
        height: '2px',
        background: 'linear-gradient(90deg, #D63AB4 0%, rgba(214,58,180,0.08) 100%)',
        borderRadius: '1px',
      }} />

      {hasVideo ? (
        <div
          style={{
            position: 'relative',
            borderRadius: '1.4rem',
            overflow: 'hidden',
            background: brand.stone900,
            aspectRatio: '16 / 9',
          }}
        >
          <video
            ref={videoRef}
            controls={isPlaying}
            preload="metadata"
            playsInline
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              background: brand.stone900,
            }}
            src={videoUrl}
          />

          {!isPlaying && (
            <button
              type="button"
              onClick={handleStartPlayback}
              aria-label={t('hero.demoCards.ai.cta', 'Play demo')}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                padding: compact ? '1rem' : '1.35rem',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.62))',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: compact ? '56px' : '68px',
                  height: compact ? '56px' : '68px',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.9)',
                  color: brand.stone900,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 16px 36px rgba(0,0,0,0.28)',
                }}
              >
                <Play size={compact ? 22 : 28} fill="currentColor" style={{ marginLeft: '3px' }} />
              </span>
              <span
                style={{
                  marginTop: compact ? '0.85rem' : '1rem',
                  fontFamily: 'var(--font-display)',
                  fontSize: compact ? '1rem' : '1.35rem',
                  color: brand.white,
                }}
              >
                {titleOverride || t('hero.demoCards.ai.panelTitle', 'Say More! demo')}
              </span>
              <span
                style={{
                  marginTop: '0.35rem',
                  fontFamily: 'var(--font-body)',
                  fontSize: compact ? '0.83rem' : '0.95rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.82)',
                }}
              >
                {t('hero.demoCards.ai.cta', 'Play demo')}
              </span>
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            borderRadius: '1.4rem',
            background: 'rgba(255,255,255,0.88)',
            minHeight: compact ? '180px' : '230px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '1rem',
          }}
        >
          <MessageSquareText size={28} color={brand.magentaDark} />
          <p
            style={{
              margin: '0.65rem 0 0.2rem 0',
              fontFamily: 'var(--font-display)',
              fontSize: compact ? '1rem' : '1.08rem',
              color: brand.stone900,
              fontWeight: 600,
            }}
          >
            {loading
              ? t('hero.demoCards.ai.loading')
              : t('hero.demoCards.ai.comingSoonTitle')}
          </p>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-body)',
              fontSize: '0.86rem',
              color: brand.stone600,
              maxWidth: '34ch',
              lineHeight: 1.45,
              fontWeight: 600,
            }}
          >
            {t('hero.demoCards.ai.comingSoonBody')}
          </p>
        </div>
      )}
    </div>
  );
}
